import { Component, OnInit } from '@angular/core';
import { HistoryMode } from 'app/core/services/HistoryMode';
import { PersonGbMasterDetailEntryMeta, PersonGbVersionDetailEntryMeta, PersonStammVersionDetailEntryMeta } from 'app/core/gb-api';
import { PersonStammMasterDetailEntryMeta, PersonstammpersonService, ZentralePersonDetailEntryMeta } from 'app/core/personstamm-api';
import { ListLoader } from 'app/shared/components/list-loader/list-loader';
import { forkJoin } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DayMonthYearDatePipe } from 'app/shared/pipes/simple/dayMonthYearDate.pipe';
import { TimepointService } from 'app/core/services/timepoint.service';
import { TranslateNamePipe } from 'app/shared/pipes/simple/translate-name.pipe';
import { FormatAhvPipe } from 'app/shared/pipes/format/format-ahv.pipe';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { ListLoaderPreloaded } from 'app/shared/components/list-loader/list-loader-preloaded';
import { AbstractComponent } from 'app/shared/components/abstract-component/abstract.component';
import { FormatUidPipe } from 'app/shared/pipes/format/format-uid.pipe';
import { DatumPipe } from 'app/shared/pipes/simple/datum.pipe';
import { NavTarget } from 'app/shared/components/link/link.component';
import { ActivatedRoute } from '@angular/router';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { KompaktPersonenadresseComponent } from '../../../shared/kompaktdarstellung/personenadresse/kompakt-personenadresse.component';
import { PermissionResponse } from '../../../core/base-api';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import HasAuskunftssperreEnum = PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum;
import PermissionsEnum = PermissionResponse.PermissionsEnum;

@Component({
  selector: 'cap-vergleich-gb-zpv',
  templateUrl: './vergleich-gb-zpv.component.html',
  styleUrls: ['./vergleich-gb-zpv.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    LinkComponent,
    KompaktPersonenadresseComponent,
    TranslatePipe
  ]
})
export class VergleichGbZpvComponent extends AbstractComponent implements OnInit {
  navTargets = NavTarget;

  personGbMaster: PersonGbMasterDetailEntryMeta;
  personGbVersion: PersonGbVersionDetailEntryMeta;
  personStamm: PersonStammMasterDetailEntryMeta;
  personStammVersion: PersonStammVersionDetailEntryMeta;
  zpvPerson: ZentralePersonDetailEntryMeta;

  listLoader: ListLoader<GbZpvVergleichVM>;
  tableEntries = Array<GbZpvVergleichVM>();

  initialized = false;
  hasPermission: boolean = false;
  hasPermissionAdresse: boolean = false;
  protected readonly PersonGbMasterDetailEntryMeta = PersonGbMasterDetailEntryMeta;

  constructor(
    private route: ActivatedRoute,
    public translate: TranslateService,
    public dayMonthYearDatePipe: DayMonthYearDatePipe,
    private zeitpunktService: TimepointService,
    private personService: PersonstammpersonService,
    private translateName: TranslateNamePipe,
    private ahvPipe: FormatAhvPipe,
    private uidPipe: FormatUidPipe,
    private datePipe: DatumPipe,
    private appPersmissionService: AppPermissionService
  ) {
    super();
    keysToTranslate<string>([
      'vergleichGbPersonZpvPerson',
      'GeburtsdatumGruendungsdatumaktivVon',
      'TodesdatumLiquidationsdatuminaktivSeit'
    ]);
  }

  ngOnInit(): void {
    this.addSub(
      this.route.data.subscribe((data) => {
        this.personGbMaster = data.entity;
        this.personGbVersion = this.personGbMaster.versions[0];
        const zentralePerson$ = this.personService.getZentralePerson(this.personGbVersion.fremdId);
        const personStamm$ = this.personService.loadById(
          this.personGbMaster.personstammMasterId.id,
          HistoryMode.HistoryMode.OHNE_HIST,
          null,
          this.zeitpunktService.timepointCtx.timepointIso,
          null
        );

        this.addSub(
          forkJoin([zentralePerson$, personStamm$]).subscribe(([zentralePerson, personStamm]) => {
            this.zpvPerson = zentralePerson;
            this.personStamm = personStamm;
            this.personStammVersion = personStamm.version;
            this.createTableElements();
          })
        );
      })
    );
    this.hasPermission = this.appPersmissionService.hasPermission(PermissionsEnum.AUSKUNFTSPERRE);
    this.hasPermissionAdresse = this.appPersmissionService.hasPermission(PermissionsEnum.ADRESSSPERRE);
  }

  createTableElements() {
    this.createEntry(
      'AHVNummer',
      this.ahvPipe.transform(this.personStammVersion.ahvNummer?.ahv11?.ahvnummer ?? ''),
      this.ahvPipe.transform(this.zpvPerson.ahvNummer)
    );
    this.createEntry(
      'AHVVersichertennummer',
      this.ahvPipe.transform(this.personStammVersion.ahvNummer?.ahv13?.ahvnummer13 ?? ''),
      this.ahvPipe.transform(this.zpvPerson.ahvNummer13)
    );
    this.createEntry('ZPVNummer', this.personGbVersion.fremdId, this.zpvPerson.fremdId, false);
    this.createEntry(
      'Anrede',
      this.translateName.transform(this.personStammVersion.anrede ? this.personStammVersion.anrede.text : null),
      this.translateName.transform(this.zpvPerson.anrede ? this.zpvPerson.anrede.text : null),
      false
    );
    this.createEntry('Name/Bezeichnung1', this.personGbVersion.name, this.zpvPerson.name);
    this.createEntry('Vorname/Bezeichnung2', this.personGbVersion.vorname, this.zpvPerson.vorname);
    this.createEntry(
      'GeburtsdatumGruendungsdatumaktivVon',
      this.dayMonthYearDatePipe.transform(this.personGbVersion.geburtsdatum),
      this.zpvPerson.geburtsdatum ? this.datePipe.transform(new Date(this.zpvPerson.geburtsdatum)) : ''
    );
    this.createEntry(
      'TodesdatumLiquidationsdatuminaktivSeit',
      this.dayMonthYearDatePipe.transform(this.personStammVersion.todesWegzugsDatum),
      this.zpvPerson.todesdatum ? this.datePipe.transform(new Date(this.zpvPerson.todesdatum)) : '',
      false
    );
    this.createEntry('Sitz', this.personGbVersion.sitz, this.zpvPerson.sitz);
    this.createEntry(
      'UID',
      this.uidPipe.transform(this.personGbVersion.unternehmensId),
      this.uidPipe.transform(this.zpvPerson.unternehmensId)
    );

    this.listLoader = new ListLoaderPreloaded(15, 1, this.tableEntries);
    this.initialized = true;
  }

  createEntry(
    titel: string,
    valueGbPerson: string,
    valueZpvPerson: string,
    gbPersonNaviegierbar: boolean = true,
    zpvPersonNaviegierbar: boolean = true
  ) {
    this.tableEntries.push(new GbZpvVergleichVM(titel, valueGbPerson, valueZpvPerson, gbPersonNaviegierbar, zpvPersonNaviegierbar));
  }

  showPersonendaten(auskunftsperre: HasAuskunftssperreEnum): boolean {
    return !this.hasAuskunftsperre(auskunftsperre) || (this.hasAuskunftsperre(auskunftsperre) && this.hasPermission);
  }

  showAdressen(auskunftsperre: HasAuskunftssperreEnum): boolean {
    return !this.hasAdresssperre(auskunftsperre) || (this.hasAdresssperre(auskunftsperre) && this.hasPermissionAdresse);
  }

  hasAuskunftsperre(auskunftsperre: HasAuskunftssperreEnum): boolean {
    return auskunftsperre === HasAuskunftssperreEnum.AUSKUNFT_SPERRE;
  }

  hasAdresssperre(auskunftsperre: PersonStammMasterDetailEntryMeta.AuskunftsperreEnum): boolean {
    return auskunftsperre === PersonStammMasterDetailEntryMeta.AuskunftsperreEnum.ADRESS_SPERRE;
  }
}

export class GbZpvVergleichVM {
  constructor(
    public titel: string,
    public valueGbPerson: string,
    public valueZpvPerson: string,
    public gbPersonNaviegierbar: boolean,
    public zpvPersonNaviegierbar: boolean
  ) {}
}
